interface CookieOptions {
  expires?: number | Date
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

export function saveCookie<T = string>(name: string, value: T, options: CookieOptions = {}): void {
  if (!name || typeof name !== 'string') {
    throw new Error('Cookie name must be a non-empty string')
  }

  if (value === undefined || value === null) {
    throw new Error('Cookie value cannot be undefined or null')
  }

  const { expires, path = '/', domain, secure = false, sameSite = 'Lax' } = options

  if (path && typeof path !== 'string') {
    throw new Error('Cookie path must be a string')
  }

  if (domain && typeof domain !== 'string') {
    throw new Error('Cookie domain must be a string')
  }

  if (expires !== undefined) {
    if (typeof expires === 'number' && expires < 0) {
      throw new Error('Cookie expiration days must be a positive number')
    }
    if (expires instanceof Date && expires < new Date()) {
      throw new Error('Cookie expiration date must be in the future')
    }
  }

  let serializedValue: string
  try {
    serializedValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
  } catch (error) {
    throw new Error(
      `Failed to serialize cookie value: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }

  let cookieString = `${encodeURIComponent(name)}=${typeof value === 'string' ? serializedValue : encodeURIComponent(serializedValue)}`

  if (expires) {
    const expiresDate =
      typeof expires === 'number' ? new Date(Date.now() + expires * 24 * 60 * 60 * 1000) : expires
    cookieString += `; expires=${expiresDate.toUTCString()}`
  }

  cookieString += `; path=${path}`

  if (domain) {
    cookieString += `; domain=${domain}`
  }

  if (secure) {
    cookieString += '; secure'
  }

  cookieString += `; SameSite=${sameSite}`

  document.cookie = cookieString
}

export function deleteCookie(name: string, options: Omit<CookieOptions, 'expires'> = {}): void {
  const { path = '/', domain } = options

  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`

  cookieString += `; path=${path}`

  if (domain) {
    cookieString += `; domain=${domain}`
  }

  document.cookie = cookieString
}

export function getCookie<T = string>(name: string): T | null {
  const nameEQ = `${encodeURIComponent(name)}=`
  const cookies = document.cookie.split(';')

  for (let cookieIndex = 0; cookieIndex < cookies.length; cookieIndex++) {
    let cookie = cookies[cookieIndex]
    if (!cookie) continue

    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1)
    }

    if (cookie.indexOf(nameEQ) === 0) {
      const value = decodeURIComponent(cookie.substring(nameEQ.length))

      try {
        return JSON.parse(value) as T
      } catch {
        return value as T
      }
    }
  }

  return null
}

export function cookieExists(name: string): boolean {
  return getCookie(name) !== null
}
